import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// Removed unused signOutUserAsync import
import { RootState } from '../store/store';
import { getUserLeases, deleteLease, updateLeaseStatus } from '../services/leaseService';
import { LeaseDocument } from '../types/lease.types';
import { useStripe } from '../contexts/FirebaseStripeContext';
import PaywallDialog from '../components/PaywallDialog';
import LeaseStatusWarningModal from '../components/LeaseStatusWarningModal';
import PDFGenerationLoader from '../components/PDFGenerationLoader';
import WaitlistEnrollmentMessage from '../components/shared/WaitlistEnrollmentMessage';
import { useTheme } from '../contexts/ThemeContext';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Snackbar,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import NavBar from '../components/NavBar/NavBar';
import StatusDropdown from '../components/StatusDropdown';
import { generateLeasePDFEnhanced } from '../utils/pdfGenerator';
import { useWaitlist } from '../hooks/useWaitlist';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);
  const { hasAccess } = useStripe();
  const { subscription } = useStripe();
  const { hasPrintingAccess, config } = useWaitlist();
  const { mode } = useTheme();

  // State for leases
  const [leases, setLeases] = useState<LeaseDocument[]>([]);
  const [leasesLoading, setLeasesLoading] = useState<boolean>(false);
  const [leasesError, setLeasesError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaseToDelete, setLeaseToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedLeaseForPrint, setSelectedLeaseForPrint] = useState<LeaseDocument | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  
  // Active lease warning modal state
  const [activeLeaseWarningOpen, setActiveLeaseWarningOpen] = useState(false);
  const [leaseToEdit, setLeaseToEdit] = useState<LeaseDocument | null>(null);

  // Welcome modal state
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

  // Fetch leases when authenticated & user changes
  useEffect(() => {
    const fetchLeases = async () => {
      if (!user?.uid) return;
      setLeasesLoading(true);
      try {
        const data = await getUserLeases(user.uid);
        // Filter out deleted leases
        const activeLeases = data.filter(lease => lease.status !== 'deleted');
        setLeases(activeLeases);
        setLeasesError(null);
      } catch (err: any) {
        console.error('Failed to fetch leases:', err);
        setLeasesError('Failed to load leases.');
      } finally {
        setLeasesLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchLeases();
    }
  }, [isAuthenticated, user?.uid]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show welcome modal on first load
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      setWelcomeModalOpen(true);
    }
  }, [isAuthenticated, isLoading]);

  const handleEditLease = (leaseId: string) => {
    const lease = leases.find(l => l.id === leaseId);
    if (lease && (lease.status === 'active' || lease.status === 'terminated')) {
      // Show warning modal for active and terminated leases
      setLeaseToEdit(lease);
      setActiveLeaseWarningOpen(true);
    } else {
      // Navigate directly for draft leases
      navigate(`/create-lease/${leaseId}`);
    }
  };

  const handleConfirmEditActiveLease = () => {
    if (leaseToEdit) {
      navigate(`/create-lease/${leaseToEdit.id}`);
      setActiveLeaseWarningOpen(false);
      setLeaseToEdit(null);
    }
  };

  const handleDeleteLease = async () => {
    if (!leaseToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteLease(leaseToDelete);
      setLeases(leases.filter(lease => lease.id !== leaseToDelete));
      setDeleteDialogOpen(false);
      setLeaseToDelete(null);
    } catch (error) {
      console.error('Failed to delete lease:', error);
      alert('Failed to delete lease. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadLease = async (lease: LeaseDocument) => {
    // Check if user has printing access
    if (!hasPrintingAccess) {
      setSnackbar({
        open: true,
        message: config.MESSAGES.PRINTING_RESTRICTED,
        severity: 'info'
      });
      return;
    }

    // Check subscription status
    if (!hasAccess) {
      setSelectedLeaseForPrint(lease);
      setPaywallOpen(true);
      return;
    }

    setIsGeneratingPDF(true);
    
    try {
      const blob = await generateLeasePDFEnhanced(lease);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${lease.name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'lease'}_agreement.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: '✅ Lease agreement downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error downloading lease:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download lease. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleStatusChange = async (leaseId: string, newStatus: 'draft' | 'active' | 'terminated') => {
    try {
      await updateLeaseStatus(leaseId, newStatus);
      
      // Update local state
      setLeases(prevLeases => 
        prevLeases.map(lease => 
          lease.id === leaseId 
            ? { ...lease, status: newStatus, updatedAt: new Date().toISOString() }
            : lease
        )
      );

      // Show success message
      setSnackbar({
        open: true,
        message: `Lease status updated to ${newStatus}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to update lease status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update lease status. Please try again.',
        severity: 'error'
      });
    }
  };

  // Helper function to display dash for missing data
  const displayValue = (value: any, fallback: string = '—') => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return fallback;
    }
    return value;
  };

  // Helper function to format property address
  const formatPropertyAddress = (property: any) => {
    if (!property?.address) {
      return { addressLine1: '—', addressLine2: '' };
    }
    
    const street = property.address.street?.trim();
    const unit = property.address.unit?.trim();
    const city = property.address.city?.trim();
    const state = property.address.state?.trim();
    
    if (!street && !city) {
      return { addressLine1: '—', addressLine2: '' };
    }
    
    const addressLine1 = street ? `${street}${unit ? ` Unit ${unit}` : ''}` : '—';
    const addressLine2 = (city && state) ? `${city}, ${state}` : (city || state || '');
    
    return { addressLine1, addressLine2 };
  };

  // Helper function to format rent amount
  const formatRentAmount = (rent: any) => {
    if (!rent?.monthlyAmount || rent.monthlyAmount <= 0) {
      return '—';
    }
    return `$${rent.monthlyAmount.toLocaleString()}`;
  };

  // Helper function to get subscription status (matching SettingsPage)
  const getSubscriptionStatus = () => {
    if (!subscription) return { label: 'No Subscription', color: 'default' as const };
    
    switch (subscription.status) {
      case 'active':
        return { 
          label: subscription.cancel_at_period_end ? 'Active (Canceling)' : 'Active', 
          color: 'success' as const 
        };
      case 'trialing':
        return { label: 'Trial', color: 'info' as const };
      case 'past_due':
        return { label: 'Past Due', color: 'warning' as const };
      case 'canceled':
        return { label: 'Canceled', color: 'error' as const };
      default:
        return { label: subscription.status, color: 'default' as const };
    }
  };

  // Helper function to format tenant names
  const formatTenantNames = (tenants: any[]) => {
    if (!tenants || tenants.length === 0) {
      return { primary: '—', additional: 0 };
    }
    
    const primaryTenant = tenants.find(t => t.isPrimary)?.fullName?.trim() || 
                         tenants[0]?.fullName?.trim();
    
    if (!primaryTenant) {
      return { primary: '—', additional: 0 };
    }
    
    return {
      primary: primaryTenant,
      additional: tenants.length > 1 ? tenants.length - 1 : 0
    };
  };

  const formatDate = (dateString: string | any) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return '—';
    }
  };

  // Removed unused getStatusColor function

  if (isLoading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: mode === 'dark' 
          ? 'linear-gradient(135deg, #1F2937 0%, #111827 100%)'
          : 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
      }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <>
      <NavBar />
      
      {/* Reusable PDF Generation Loader */}
      <PDFGenerationLoader open={isGeneratingPDF} />
      
      <Box sx={{ 
        minHeight: '100vh',
        background: mode === 'dark'
          ? 'linear-gradient(135deg, #111827 0%, #1F2937 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        pt: 4,
        pb: 6
      }}>
        <Container maxWidth="lg">
          {/* Waitlist Enrollment Message */}
          <WaitlistEnrollmentMessage />

          {/* Welcome Section */}
          <Card 
            elevation={0} 
            sx={{ 
              mb: 4, 
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #374151 0%, #4B5563 50%, #6B7280 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%)',
              color: 'white',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Welcome back, {userProfile?.firstName || user?.displayName || 'User'}!
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Manage your lease agreements all in one place
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { md: 'right' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/create-lease')}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    Create New Lease
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={0} sx={{ 
                borderRadius: 2, 
                border: '1px solid', 
                borderColor: 'divider',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                '&:hover': {
                  boxShadow: mode === 'dark' 
                    ? '0 4px 12px rgba(0,0,0,0.3)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DescriptionIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Total Leases
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {leases.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={0} sx={{ 
                borderRadius: 2, 
                border: '1px solid', 
                borderColor: 'divider',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                '&:hover': {
                  boxShadow: mode === 'dark' 
                    ? '0 4px 12px rgba(0,0,0,0.3)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EditIcon sx={{ color: 'warning.main', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Drafts
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {leases.filter(l => l.status === 'draft').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={0} sx={{ 
                borderRadius: 2, 
                border: '1px solid', 
                borderColor: 'divider',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                '&:hover': {
                  boxShadow: mode === 'dark' 
                    ? '0 4px 12px rgba(0,0,0,0.3)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DashboardIcon sx={{ color: 'success.main', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Active
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {leases.filter(l => l.status === 'active').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={0} sx={{ 
                borderRadius: 2, 
                border: '1px solid', 
                borderColor: 'divider',
                bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
                '&:hover': {
                  boxShadow: mode === 'dark' 
                    ? '0 4px 12px rgba(0,0,0,0.3)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {hasPrintingAccess ? 'Membership' : 'Access Status'}
                    </Typography>
                  </Box>
                  <Box pt={3}>
                    {hasPrintingAccess ? (
                      <Chip 
                        label={getSubscriptionStatus().label}
                        color={getSubscriptionStatus().color}
                        icon={hasAccess ? <CheckCircleIcon /> : undefined}
                      />
                    ) : (
                      <Chip 
                        label="Waitlist Member"
                        color="warning"
                        icon={<InfoIcon />}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Lease Management Table */}
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            bgcolor: mode === 'dark' ? 'background.paper' : 'background.default',
            border: mode === 'dark' ? '1px solid' : 'none',
            borderColor: mode === 'dark' ? 'divider' : 'transparent'
          }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                // bgcolor: mode === 'dark' ? 'grey.800' : 'grey.50'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Your Leases
                </Typography>
              </Box>
              
              {leasesLoading ? (
                <Box sx={{ p: 3 }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : leasesError ? (
                <Alert severity="error" sx={{ m: 3 }}>
                  {leasesError}
                </Alert>
              ) : leases.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <DescriptionIcon sx={{ 
                    fontSize: 64, 
                    color: mode === 'dark' ? 'grey.600' : 'text.disabled', 
                    mb: 2 
                  }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No leases yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first lease agreement to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/create-lease')}
                    sx={{ borderRadius: 2 }}
                  >
                    Create Your First Lease
                  </Button>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Property Address</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Tenant</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Rent</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Start Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }}>Last Updated</TableCell>
                        <TableCell sx={{ fontWeight: 600, border: 'none' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leases.map((lease) => (
                        <TableRow 
                          key={lease.id} 
                          onClick={() => handleEditLease(lease.id)}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: 'primary.50',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              '& .MuiTableCell-root': {
                                borderColor: 'primary.200'
                              }
                            },
                            '&:last-child td, &:last-child th': { border: 0 },
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {displayValue(lease.name)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const addressInfo = formatPropertyAddress(lease.property);
                              return (
                                <>
                                  <Typography variant="body2">
                                    {addressInfo.addressLine1}
                                  </Typography>
                                  {addressInfo.addressLine2 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {addressInfo.addressLine2}
                                    </Typography>
                                  )}
                                </>
                              );
                            })()}
                          </TableCell>
                                                      <TableCell>
                              <Typography variant="body2">
                                {(() => {
                                  const tenantInfo = formatTenantNames(lease.tenants);
                                  return (
                                    <>
                                      {tenantInfo.primary}
                                      {tenantInfo.additional > 0 && (
                                        <Typography variant="caption" color="text.secondary" component="div">
                                          +{tenantInfo.additional} more
                                        </Typography>
                                      )}
                                    </>
                                  );
                                })()}
                              </Typography>
                            </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatRentAmount(lease.rent)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {lease.terms?.startDate ? formatDate(lease.terms.startDate) : '—'}
                            </Typography>
                          </TableCell>
                                                      <TableCell onClick={(e) => e.stopPropagation()}>
                             <StatusDropdown
                               currentStatus={lease.status as 'draft' | 'active' | 'terminated'}
                               leaseId={lease.id}
                               onStatusChange={handleStatusChange}
                             />
                            </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(lease.updatedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditLease(lease.id)}
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download PDF">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDownloadLease(lease)}
                                    sx={{ 
                                      color: hasPrintingAccess ? 'success.main' : 'text.disabled',
                                      opacity: hasPrintingAccess ? 1 : 0.5
                                    }}
                                    disabled={isGeneratingPDF || !hasPrintingAccess}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => {
                                      setLeaseToDelete(lease.id);
                                      setDeleteDialogOpen(true);
                                    }}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Lease</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this lease? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteLease} 
            color="error" 
            disabled={isDeleting}
            variant="contained"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for status updates */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Paywall Dialog */}
      <PaywallDialog
        open={paywallOpen}
        onClose={() => {
          setPaywallOpen(false);
          setSelectedLeaseForPrint(null);
        }}
      />

      {/* Active/Terminated Lease Warning Modal */}
      <LeaseStatusWarningModal
        open={activeLeaseWarningOpen}
        onClose={() => {
          setActiveLeaseWarningOpen(false);
          setLeaseToEdit(null);
        }}
        onConfirm={handleConfirmEditActiveLease}
        leaseName={leaseToEdit?.name}
        leaseStatus={leaseToEdit?.status === 'active' || leaseToEdit?.status === 'terminated' ? leaseToEdit.status : undefined}
      />

      {/* Welcome Modal - Coinbase Style */}
      <Dialog
        open={welcomeModalOpen}
        onClose={() => setWelcomeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            border: '1px solid #E5E7EB',
            bgcolor: '#ffffff'
          }
        }}
      >
        <DialogContent sx={{ p: 5, textAlign: 'center' }}>
          {/* Success Icon */}
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#0052FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 4px 16px rgba(0, 82, 255, 0.2)'
          }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          {/* Heading */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#0A0B0D',
              mb: 2,
              letterSpacing: '-0.02em',
              fontSize: '2rem'
            }}
          >
            Thank you for signing up!
          </Typography>

          {/* Welcome Message */}
          <Typography
            sx={{
              fontSize: '1.125rem',
              color: '#3A3F4C',
              mb: 3,
              lineHeight: 1.6
            }}
          >
            Welcome to <Box component="span" sx={{
              color: '#0052FF',
              fontWeight: 600
            }}>TenantTerms</Box>. We're excited to have you on board.
          </Typography>

          {/* Info Box */}
          <Box
            sx={{
              p: 3,
              bgcolor: '#F3F4F6',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              textAlign: 'left',
              mb: 4
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <InfoIcon sx={{ color: '#0052FF', fontSize: 24, mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#0A0B0D',
                    mb: 0.5,
                    fontSize: '0.95rem'
                  }}
                >
                  Important Notice
                </Typography>
                <Typography
                  sx={{
                    color: '#5B616E',
                    fontSize: '0.9rem',
                    lineHeight: 1.6
                  }}
                >
                  Lease printing will be available once our lease templates have been updated to reflect the latest laws and regulations. <Box component="span" sx={{ color: '#0052FF', fontWeight: 600 }}>We'll send you an email once ready.</Box> We appreciate your patience!
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* CTA Button */}
          <Button
            onClick={() => setWelcomeModalOpen(false)}
            fullWidth
            sx={{
              height: '56px',
              background: '#0052FF',
              color: 'white',
              borderRadius: '999px',
              fontSize: '1.05rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#0041CC',
                boxShadow: 'none'
              }
            }}
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DashboardPage; 